import { css } from '@emotion/react';
import { IonButton, IonCol, IonIcon, IonPopover, IonRow, IonSelect, IonSelectOption, IonText } from '@ionic/react';
import { informationCircleOutline } from 'ionicons/icons';
import React from 'react';
import { AqiStandardProperties } from '../../common/analytics/analytics-properties';
import { AnalyticsEventName } from '../../common/analytics/types';
import { trackEvent } from '../../common/analytics/utils';
import { useSettings } from '../../contexts/SettingsContext';
import * as commonCss from '../../styles/common';
import { AqiInfo } from '../AqiInfo';
import { SkeletonText } from '../SkeletonText';

interface AqiScaleProps {
  popOverSide?: 'top' | 'bottom' | 'left' | 'right';
  popOverAlignment?: 'start' | 'end' | 'center';
  popOverOffsetY?: string;
}

export const AqiScale: React.FC<AqiScaleProps> = ({
  popOverSide = 'right',
  popOverAlignment = 'start',
  popOverOffsetY,
}) => {
  const { aqiStandard, availableAqiStandards, pollutantConfigs, setAqiStandardId } = useSettings();

  const handleAqiInfoOpened = React.useCallback(() => {
    if (!aqiStandard) {
      return;
    }
    const eventProperties: AqiStandardProperties = {
      aqiStandardId: aqiStandard.aqiStandardId,
      name: aqiStandard.name,
      aqiClassType: aqiStandard.aqiClassType,
      pollutantConfigs: aqiStandard.pollutantConfigs.map((config) => ({
        pollutant: config.pollutant,
        displayName: config.displayName,
      })),
    };
    trackEvent(AnalyticsEventName.OpenedAqiInfoPanel, eventProperties);
  }, [aqiStandard]);

  if (!availableAqiStandards || !aqiStandard || !pollutantConfigs) {
    return null;
  }

  const AQI_SCALE_CSS = {
    aqiHeader: [
      css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--ion-color-secondary)',
        fontWeight: 'bold',
      }),
    ],
    aqiInfoButton: [commonCss.noPadding, commonCss.noMargin],
    popover: [
      css({
        '--width': '25rem',
        '--offset-y': popOverOffsetY && `${popOverOffsetY}`,
      }),
    ],
    aqiInfoIcon: [
      css({
        color: 'var(--ion-color-secondary)',
      }),
    ],
    aqiSelectorContainer: [
      css({
        display: 'flex',
        justifyContent: 'center',
        // manual adjustment to keep the select box at the same height as the text
        height: '27px',
        marginTop: '-18px',
      }),
    ],
    aqiSelector: [
      css({
        '--highlight-color-focused': 'transparent',
      }),
    ],
    aqiHorizontalBar: [
      css({
        display: 'flex',
        width: '100%',
        height: 5,
        borderRadius: 2,
      }),
    ],
    aqiDescriptions: [
      commonCss.textAlign.end,
      css({
        backgroundColor: 'var(--background-color)',
        flex: 1,
        fontSize: `min(${commonCss.SMALL_FONT_SIZE_REM}rem, 3.2vw)`,
      }),
    ],
  };

  return (
    <>
      <IonRow css={commonCss.smallText}>
        <IonCol css={[commonCss.noPadding, AQI_SCALE_CSS.aqiHeader]}>
          {availableAqiStandards.length < 2 ? (
            <IonText>{aqiStandard.name}</IonText>
          ) : (
            <div css={AQI_SCALE_CSS.aqiSelectorContainer}>
              <IonSelect
                value={aqiStandard.aqiStandardId}
                aria-label="Aqi Standard Selector"
                interface="popover"
                css={AQI_SCALE_CSS.aqiSelector}
                onIonChange={(e) => {
                  const newAqiStandardId = e.detail.value;
                  setAqiStandardId(newAqiStandardId);
                }}
              >
                {availableAqiStandards.map((standard) => (
                  <IonSelectOption key={standard.aqiStandardId} value={standard.aqiStandardId}>
                    {standard.name}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </div>
          )}
          <IonButton fill="clear" size="small" id="btn-aqi-info" css={AQI_SCALE_CSS.aqiInfoButton}>
            <IonIcon css={AQI_SCALE_CSS.aqiInfoIcon} icon={informationCircleOutline} />
          </IonButton>
          <IonPopover
            trigger="btn-aqi-info"
            css={AQI_SCALE_CSS.popover}
            onIonPopoverDidPresent={handleAqiInfoOpened}
            side={popOverSide}
            alignment={popOverAlignment}
          >
            <AqiInfo pollutantConfigs={pollutantConfigs} aqiStandard={aqiStandard}></AqiInfo>
          </IonPopover>
        </IonCol>
      </IonRow>
      <IonRow>
        {aqiStandard ? (
          <div css={AQI_SCALE_CSS.aqiHorizontalBar}>
            {aqiStandard.aqiLevels.map(({ aqiLevelId, color }) => {
              // Note: here we use the `style` attribute to set CSS custom properties
              // for performance reasons as explained in the documentation here:
              // https://emotion.sh/docs/best-practices#advanced-css-variables-with-style
              return (
                <div
                  key={aqiLevelId}
                  css={AQI_SCALE_CSS.aqiDescriptions}
                  style={{ '--background-color': color } as React.CSSProperties}
                ></div>
              );
            })}
          </div>
        ) : (
          <SkeletonText height={20} />
        )}
      </IonRow>
      <IonRow css={commonCss.smallText}>
        {aqiStandard && (
          <>
            <IonCol css={commonCss.noPadding}>
              <IonText>{aqiStandard.aqiLevels[0].description}</IonText>
            </IonCol>
            <IonCol css={[commonCss.noPadding, commonCss.textAlign.end]}>
              <IonText>{aqiStandard.aqiLevels[aqiStandard.aqiLevels.length - 1].description}</IonText>
            </IonCol>
          </>
        )}
      </IonRow>
    </>
  );
};
